import { getDB } from "./schema.js";
import { nowIso } from "../utils/dates.js";
import { PLAYED_STATUSES } from "../components/status-badge.js";

const STORE = "rankings";
// Each media type gets its own ranking record, keyed by this id in the shared
// "rankings" store. Defaults to "videogame" so existing games callers are
// untouched; books pass "book".
const DEFAULT_MEDIA = "videogame";
const PLAYED = new Set(PLAYED_STATUSES);

export function isEligible(item) {
	return PLAYED.has(item.engagement_status);
}

// --- persistence ---

export async function getRankingState(media = DEFAULT_MEDIA) {
	const db = await getDB();
	const rec = await db.get(STORE, media);
	return (
		rec ?? {
			id: media,
			order: [],
			active: [],
			pending: null,
			last: null,
			updated_at: null,
		}
	);
}

export async function saveRankingState(state, media = DEFAULT_MEDIA) {
	const db = await getDB();
	const rec = { ...state, id: media, updated_at: nowIso() };
	await db.put(STORE, rec);
	return rec;
}

export async function resetRanking(media = DEFAULT_MEDIA) {
	return saveRankingState(
		{ order: [], active: [], pending: null, last: null },
		media,
	);
}

// --- pure logic (binary insertion sort, ranking is best → worst) ---
//
// Several games are inserted concurrently (`state.active`, an array of
// { gameId, lo, hi } insertions) so consecutive battles involve different
// games — the matchup order is unpredictable, but the resulting ranking is
// identical to a one-at-a-time binary insertion sort. `state.pending` is the
// gameId of the insertion currently being battled; `state.last` is the one
// just resolved (avoided when picking the next, to break up repeats).

// How many insertions to keep in flight at once. More = less predictable, but
// also more games to hold in your head.
const CONCURRENCY = 3;

// Read insertions tolerating the old single-insertion (`current`) shape.
function activeOf(state) {
	if (Array.isArray(state.active)) return state.active;
	return state.current ? [state.current] : [];
}

// When a game is spliced into `order` at index `p`, every other in-flight
// insertion's bounds (expressed in pre-splice indices) must shift. Indices
// strictly above the insertion point slide right; an insertion landing inside
// an open window widens that window by one (it's a new, unjudged neighbour).
function shiftInsertion(ins, p) {
	let { lo, hi } = ins;
	if (p < lo) {
		lo += 1;
		hi += 1;
	} else if (p <= hi) {
		hi += 1;
	}
	return { ...ins, lo, hi };
}

// Drop anything no longer eligible (deleted or status changed) and clamp each
// in-progress insertion so it stays valid as the pool shifts under it.
export function sanitizeState(state, eligibleIds) {
	const eligible = new Set(eligibleIds);
	const order = (state.order ?? []).filter((id) => eligible.has(id));
	const placed = new Set(order);
	const seen = new Set();
	const active = [];
	for (const ins of activeOf(state)) {
		// Skip ineligible, already-placed, or duplicate insertions.
		if (!eligible.has(ins.gameId) || placed.has(ins.gameId)) continue;
		if (seen.has(ins.gameId)) continue;
		const hi = Math.min(ins.hi, order.length);
		const lo = Math.min(ins.lo, hi);
		if (lo >= hi) continue;
		seen.add(ins.gameId);
		active.push({ gameId: ins.gameId, lo, hi });
	}
	const activeIds = new Set(active.map((a) => a.gameId));
	const pending = activeIds.has(state.pending) ? state.pending : null;
	const last = eligible.has(state.last) ? state.last : null;
	return { ...state, current: undefined, order, active, pending, last };
}

function pickUnplaced(order, active, eligibleIds, rng) {
	const taken = new Set(order);
	for (const a of active) taken.add(a.gameId);
	const pool = eligibleIds.filter((id) => !taken.has(id));
	if (pool.length === 0) return null;
	return pool[Math.floor(rng() * pool.length)];
}

// Returns { state, battle }. `battle` is { challengerId, opponentId } or null
// when every eligible game is ranked. Seeds the first game and tops up the
// in-flight insertions, so the caller must persist the returned state.
export function computeNext(state, eligibleIds, rng = Math.random) {
	let order = state.order;
	let active = activeOf(state).slice();

	// Seat the very first game — nothing to compare it against yet.
	if (order.length === 0) {
		const first = pickUnplaced(order, active, eligibleIds, rng);
		if (first == null) return { state, battle: null };
		order = [first];
	}

	// Top up in-flight insertions from the unplaced pool.
	while (active.length < CONCURRENCY) {
		const challenger = pickUnplaced(order, active, eligibleIds, rng);
		if (challenger == null) break;
		active.push({ gameId: challenger, lo: 0, hi: order.length });
	}

	if (active.length === 0) {
		return { state: { ...state, order, active }, battle: null };
	}

	// Keep showing the current matchup across refreshes; otherwise pick a fresh
	// insertion at random, avoiding the one just resolved.
	let pending = state.pending;
	let chosen = active.find((a) => a.gameId === pending);
	if (!chosen) {
		let pool = active.filter((a) => a.gameId !== state.last);
		if (pool.length === 0) pool = active;
		chosen = pool[Math.floor(rng() * pool.length)];
		pending = chosen.gameId;
	}

	const mid = Math.floor((chosen.lo + chosen.hi) / 2);
	return {
		state: { ...state, order, active, pending },
		battle: { challengerId: chosen.gameId, opponentId: order[mid] },
	};
}

// Apply the user's pick to the pending insertion. `challengerWon` = the
// challenger ranks above the opponent.
export function resolveBattle(state, challengerWon) {
	const active = activeOf(state).slice();
	const idx = active.findIndex((a) => a.gameId === state.pending);
	if (idx === -1) return state;

	const { gameId, lo, hi } = active[idx];
	const mid = Math.floor((lo + hi) / 2);
	let nlo = lo;
	let nhi = hi;
	if (challengerWon) nhi = mid;
	else nlo = mid + 1;

	if (nlo >= nhi) {
		// Placed: splice into the ranking and shift the other insertions.
		const order = state.order.slice();
		order.splice(nlo, 0, gameId);
		const rest = active
			.filter((_, i) => i !== idx)
			.map((a) => shiftInsertion(a, nlo));
		return { ...state, order, active: rest, pending: null, last: gameId };
	}

	active[idx] = { gameId, lo: nlo, hi: nhi };
	return { ...state, active, pending: null, last: gameId };
}

export function rankingProgress(state, eligibleIds) {
	const eligible = new Set(eligibleIds);
	const placed = (state.order ?? []).filter((id) => eligible.has(id)).length;
	return { placed, total: eligibleIds.length };
}
